import React, { useState, useMemo } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import "./index.css";
import selectedCheck from "../../../../../assets/design-revamp-company/company-epr/selected-check-erp.svg";
import unselectedCheck from "../../../../../assets/design-revamp-company/company-epr/unselected-check-erp.svg";
import leftBtn from "../../../../../assets/design-revamp-company/company-epr/left-btn.svg";
import rightBtn from "../../../../../assets/design-revamp-company/company-epr/right-btn.svg";
import selectAllUnselected from "../../../../../assets/design-revamp-company/company-epr/select-all-unselected.svg";
import selectAllLeftArrow from "../../../../../assets/design-revamp-company/company-epr/left-arrow-select-all.svg";
import selectAllRightArrow from "../../../../../assets/design-revamp-company/company-epr/right-arrow-select-all.svg";
import sortingIcon from "../../../../../assets/design-revamp/default-sort.svg";
import financialLedgerIcon from "../../../../../assets/design-revamp-company/company-epr/financial-ledger-icon.svg";
import infoIcon from "../../../../../assets/design-revamp-company/company-epr/info-icon.svg";

const ItemTypes = {
  MAIN_TABLE_ROW: "mainTableRow",
  TRASH_TABLE_ROW: "trashTableRow",
  CONTINUE_TABLE_ROW: "continueTableRow",
};

const DraggableRow = ({
  item,
  isSelected,
  onSelect,
  onMoveLeft,
  onMoveRight,
  defaultGrouped = false,
}) => {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.MAIN_TABLE_ROW,
    item: {
      id: item.id,
      tableType: ItemTypes.MAIN_TABLE_ROW,
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  return (
    <tr
      ref={drag}
      className={`table-row ${isDragging ? "dragging" : ""} ${
        isSelected ? "selected" : ""
      }`}
    >
      <td>{item.account_number}</td>
      <td>{item.account_name}</td>
      <td>
        <div
          onClick={() => onSelect(item.id, !isSelected)}
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={isSelected ? selectedCheck : unselectedCheck}
            alt={isSelected ? "Selected" : "Unselected"}
            style={{ width: "24px", height: "24px" }}
          />
        </div>
      </td>
      <div className="actions">
        <button
          onClick={() => onMoveLeft(item)}
          className="btn-action btn-left"
          title="Move to trash"
          style={{ position: "absolute", left: "-30px", top: "25%" }}
        >
          <img src={leftBtn} alt="arrow left to bin" width={16} height={16} />
        </button>
        <button
          onClick={() => onMoveRight(item)}
          className="btn-action btn-right"
          title="Continue"
          style={{ position: "absolute", right: "-30px", top: "25%" }}
        >
          <img src={rightBtn} alt="arrow left to bin" width={16} height={16} />
        </button>
      </div>
    </tr>
  );
};

// The rest of the component remains the same as in the original file
const DropZone = ({ side, onDrop, children }) => {
  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.MAIN_TABLE_ROW,
    drop: (item, monitor) => onDrop(item),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <div ref={drop} className={`drop-zone ${side} ${isOver ? "over" : ""}`}>
      {children}
    </div>
  );
};

const DragDropTable = ({
  data,
  groupedData,
  onUpdateData,
  onMoveToTrash,
  onMoveToContinue,
  defaultGrouped = true,
  continueBin = [],
}) => {
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [showGrouped, setShowGrouped] = useState(defaultGrouped);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Trash and Continue state
  const [trashItems, setTrashItems] = useState([]);
  const [continueItems, setContinueItems] = useState(
    continueBin && continueBin.length > 0 ? continueBin : []
  );
  const [selectedTrashItems, setSelectedTrashItems] = useState(new Set());
  const [selectedContinueItems, setSelectedContinueItems] = useState(new Set());

  // Sorting function
  const sortedData = useMemo(() => {
    let sortableData = [...(showGrouped ? groupedData : data)];
    if (sortConfig.key !== null) {
      sortableData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [data, groupedData, sortConfig, showGrouped]);

  // Pagination
  const paginatedData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * itemsPerPage;
    const lastPageIndex = firstPageIndex + itemsPerPage;
    return sortedData.slice(firstPageIndex, lastPageIndex);
  }, [sortedData, currentPage, itemsPerPage]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleSelect = (id, isSelected) => {
    if (showGrouped) {
      // For grouped view, select all original items in the group
      const group = groupedData.find((g) => g.id === id);
      if (group) {
        setSelectedItems((prev) => {
          const newSelection = new Set(prev);
          group.originalIds.forEach((originalId) => {
            if (isSelected) {
              newSelection.add(originalId);
            } else {
              newSelection.delete(originalId);
            }
          });
          return newSelection;
        });
      }
    } else {
      // For regular view
      setSelectedItems((prev) => {
        const newSelection = new Set(prev);
        if (isSelected) {
          newSelection.add(id);
        } else {
          newSelection.delete(id);
        }
        return newSelection;
      });
    }
  };

  const handleSelectAll = () => {
    if (selectedItems.size === data.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(data.map((item) => item.id)));
    }
  };

  const handleMoveLeft = (item) => {
    if (showGrouped) {
      // Handle grouped items
      const group = groupedData.find((g) => g.id === item.id);
      if (group) {
        const itemsToMove = data.filter((d) =>
          group.originalIds.includes(d.id)
        );
        const newData = data.filter((d) => !group.originalIds.includes(d.id));
        onUpdateData(newData);
        itemsToMove.forEach((item) => onMoveToTrash(item));
      }
    } else {
      // Handle single item
      const newData = data.filter((dataItem) => dataItem.id !== item.id);
      onUpdateData(newData);
      onMoveToTrash(item);
    }
    setTrashItems([...trashItems, item]);
    setSelectedItems(new Set());
  };

  const handleMoveRight = (item) => {
    if (showGrouped) {
      // Handle grouped items
      const group = groupedData.find((g) => g.id === item.id);
      if (group) {
        const itemsToMove = data.filter((d) =>
          group.originalIds.includes(d.id)
        );
        const newData = data.filter((d) => !group.originalIds.includes(d.id));
        onUpdateData(newData);
        itemsToMove.forEach((item) => onMoveToContinue(item));
      }
    } else {
      // Handle single item
      const newData = data.filter((dataItem) => dataItem.id !== item.id);
      onUpdateData(newData);
      onMoveToContinue(item);
    }
    setContinueItems([...continueItems, item]);
    setSelectedItems(new Set());
  };

  // Handlers for trash and continue tables
  const handleTrashToMain = (selectedTrashItem) => {
    // Move selected trash items back to main data
    onUpdateData([...data, selectedTrashItem]);
    setTrashItems(
      trashItems.filter((item) => item.id !== selectedTrashItem.id)
    );
  };

  const handleContinueToMain = (selectedContinueItem) => {
    // Move selected continue items back to main data
    onUpdateData([...data, selectedContinueItem]);
    setContinueItems(
      continueItems.filter((item) => item.id !== selectedContinueItem.id)
    );
  };

  const handleTrashSelectAll = () => {
    const allTrashIds = new Set(trashItems.map((item) => item.id));
    setSelectedTrashItems(
      selectedTrashItems.size === trashItems.length ? new Set() : allTrashIds
    );
  };

  const handleContinueSelectAll = () => {
    const allContinueIds = new Set(continueItems.map((item) => item.id));
    setSelectedContinueItems(
      selectedContinueItems.size === continueItems.length
        ? new Set()
        : allContinueIds
    );
  };

  const handleBulkMove = (direction) => {
    const selectedItemsArray = data.filter((item) =>
      selectedItems.has(item.id)
    );
    const remainingItems = data.filter((item) => !selectedItems.has(item.id));
    onUpdateData(remainingItems);

    if (direction === "left") {
      selectedItemsArray.forEach((item) => onMoveToTrash(item));
    } else {
      selectedItemsArray.forEach((item) => onMoveToContinue(item));
    }
    setSelectedItems(new Set());
  };

  const handleDrop = (sourceItem, targetTable) => {
    const sourceTable = sourceItem.tableType;

    // Function to remove item from source table
    const removeFromSourceTable = (item) => {
      switch (sourceTable) {
        case ItemTypes.MAIN_TABLE_ROW:
          onUpdateData(data.filter((d) => d.id !== item.id));
          break;
        case ItemTypes.TRASH_TABLE_ROW:
          setTrashItems(trashItems.filter((d) => d.id !== item.id));
          break;
        case ItemTypes.CONTINUE_TABLE_ROW:
          setContinueItems(continueItems.filter((d) => d.id !== item.id));
          break;
        default:
          break;
      }
    };

    // Function to add item to target table
    const addToTargetTable = (item) => {
      switch (targetTable) {
        case "main":
          onUpdateData([...data, item]);
          break;
        case "trash":
          setTrashItems([...trashItems, item]);
          break;
        case "continue":
          setContinueItems([...continueItems, item]);
          break;
        default:
          break;
      }
    };

    // Find the actual item
    let foundItem;
    switch (sourceTable) {
      case ItemTypes.MAIN_TABLE_ROW:
        foundItem = data.find((d) => d.id === sourceItem.id);
        break;
      case ItemTypes.TRASH_TABLE_ROW:
        foundItem = trashItems.find((d) => d.id === sourceItem.id);
        break;
      case ItemTypes.CONTINUE_TABLE_ROW:
        foundItem = continueItems.find((d) => d.id === sourceItem.id);
        break;
      default:
        break;
    }

    // If item found, move it
    if (foundItem) {
      removeFromSourceTable(foundItem);
      addToTargetTable(foundItem);
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="drag-drop-container">
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div className="account-tab-header">Irrelevant accounts</div>
          <DropZone side="left" onDrop={(item) => handleDrop(item, "trash")}>
            <div className="table-container table-trash">
              <div className="table-actions">
                <div className="selected-number">
                  <p
                    style={{
                      color: "#0C376A",
                      fontSize: "14px",
                      fontWeight: "bold",
                      marginBottom: 0,
                    }}
                  >
                    {selectedTrashItems.size === trashItems.length &&
                    trashItems.length !== 0
                      ? "All Selected"
                      : selectedTrashItems.size === 0
                      ? "None Selected"
                      : "Multiple Selected"}
                  </p>
                </div>
                <div className="bulk-actions">
                  <button
                    onClick={handleTrashSelectAll}
                    className="btn btn-primary"
                  >
                    {selectedTrashItems.size === trashItems.length &&
                    trashItems.length !== 0 ? (
                      <img
                        src={selectedCheck}
                        alt="All selected"
                        width={16}
                        height={16}
                      />
                    ) : (
                      <img
                        src={selectAllUnselected}
                        alt="Not selected"
                        width={16}
                        height={16}
                      />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      // Bulk move from trash to main data
                      const selectedItemsArray = trashItems.filter((item) =>
                        selectedTrashItems.has(item.id)
                      );
                      onUpdateData([...data, ...selectedItemsArray]);
                      setTrashItems(
                        trashItems.filter(
                          (item) => !selectedTrashItems.has(item.id)
                        )
                      );
                      setSelectedTrashItems(new Set());
                    }}
                    className="btn btn-success"
                    disabled={selectedTrashItems.size === 0}
                  >
                    <img
                      src={selectAllRightArrow}
                      alt="Move back to main"
                      width={16}
                      height={16}
                    />
                  </button>
                </div>
              </div>
              <table className="data-table">
                <thead>
                  <tr style={{ height: "50px" }}></tr>
                </thead>
                <tbody>
                  {trashItems.map((item) => (
                    <tr key={item.id} className="table-row">
                      <td>{item.account_number}</td>
                      <td>{item.account_name}</td>
                      <td>
                        <div
                          onClick={() => {
                            const newSelectedTrashItems = new Set(
                              selectedTrashItems
                            );
                            if (newSelectedTrashItems.has(item.id)) {
                              newSelectedTrashItems.delete(item.id);
                            } else {
                              newSelectedTrashItems.add(item.id);
                            }
                            setSelectedTrashItems(newSelectedTrashItems);
                          }}
                          style={{ cursor: "pointer", textAlign: "center" }}
                        >
                          <img
                            src={
                              selectedTrashItems.has(item.id)
                                ? selectedCheck
                                : unselectedCheck
                            }
                            alt="Select"
                            style={{ width: "24px", height: "24px" }}
                          />
                        </div>
                      </td>
                      <div className="actions">
                        <button
                          onClick={() => handleTrashToMain(item)}
                          className="btn-action"
                          style={{
                            position: "absolute",
                            right: "-30px",
                            top: "25%",
                          }}
                        >
                          <img
                            src={rightBtn}
                            alt="Move to main"
                            width={16}
                            height={16}
                          />
                        </button>
                      </div>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DropZone>
        </div>

        <div style={{ display: "flex", flexDirection: "column" }}>
          <div
            className="account-tab-header"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <img
              src={financialLedgerIcon}
              alt="Financial Ledger Icon"
              width={20}
              height={20}
            />
            <p style={{ marginBottom: 0 }}>Financial Ledger</p>
            <img src={infoIcon} alt="Info icon" width={20} height={20} />
          </div>
          <div className="table-container">
            <div className="table-actions">
              <div className="sorting-erp" style={{ cursor: "pointer" }}>
                <img
                  src={sortingIcon}
                  alt="Sorting Icon"
                  width={18}
                  height={18}
                  onClick={() => handleSort("account_number")}
                />
              </div>
              <div className="selected-number">
                <p
                  style={{
                    color: "#0C376A",
                    fontSize: "14px",
                    fontWeight: "bold",
                    marginBottom: 0,
                  }}
                >
                  {selectedItems.size === data.length
                    ? "All Selected"
                    : selectedItems.size === 0
                    ? "None Selected"
                    : "Multiple Selected"}
                </p>
              </div>
              <div className="bulk-actions" style={{ cursor: "pointer" }}>
                <button
                  onClick={() => handleBulkMove("left")}
                  className="btn btn-danger"
                  disabled={selectedItems.size === 0}
                >
                  <img
                    src={selectAllLeftArrow}
                    alt="Select all to bin"
                    width={16}
                    height={16}
                  />
                </button>
                <button onClick={handleSelectAll} className="btn btn-primary">
                  {selectedItems.size === data.length ? (
                    <img
                      src={selectedCheck}
                      alt="All selected"
                      width={16}
                      height={16}
                    />
                  ) : (
                    <img
                      src={selectAllUnselected}
                      alt="Not selected"
                      width={16}
                      height={16}
                    />
                  )}
                </button>
                <button
                  onClick={() => handleBulkMove("right")}
                  className="btn btn-success"
                  disabled={selectedItems.size === 0}
                >
                  <img
                    src={selectAllRightArrow}
                    alt="Select all to continue"
                    width={16}
                    height={16}
                  />
                </button>
              </div>
            </div>

            <table className="data-table">
              <thead>
                <tr style={{ height: "50px" }}>
                  {/* <th></th>
                <th></th>
                <th className="checkbox-column"></th>
                <th className="actions-column"></th> */}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item) => (
                  <DraggableRow
                    key={item.id}
                    item={item}
                    isSelected={
                      showGrouped
                        ? item.originalIds?.every((id) => selectedItems.has(id))
                        : selectedItems.has(item.id)
                    }
                    onSelect={handleSelect}
                    onMoveLeft={handleMoveLeft}
                    onMoveRight={handleMoveRight}
                    defaultGrouped={showGrouped}
                  />
                ))}
              </tbody>
            </table>
            {/* Pagination */}
            <div className="pagination">
              <select
                className="btn-pagination"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option className="pagination-text" value={10}>
                  Show 10
                </option>
                <option className="pagination-text" value={20}>
                  Show 20
                </option>
                <option className="pagination-text" value={50}>
                  Show 50
                </option>
                <option className="pagination-text" value={100}>
                  Show 100
                </option>
              </select>
              <div className="pagination-controls">
                <button
                  className="btn-pagination pagination-text"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="pagination-pages">
                  {currentPage} / {Math.ceil(sortedData.length / itemsPerPage)}
                </span>
                <button
                  className="btn-pagination pagination-text"
                  onClick={() =>
                    setCurrentPage((prev) =>
                      prev < Math.ceil(sortedData.length / itemsPerPage)
                        ? prev + 1
                        : prev
                    )
                  }
                  disabled={
                    currentPage === Math.ceil(sortedData.length / itemsPerPage)
                  }
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column" }}>
          <div className="account-tab-header">
            Chosen accounts with invoices
          </div>
          <DropZone
            side="right"
            accept={[ItemTypes.MAIN_TABLE_ROW, ItemTypes.TRASH_TABLE_ROW]}
            onDrop={(item) => handleDrop(item, "continue")}
          >
            <div className="table-container table-continue">
              <div className="table-actions">
                <div className="bulk-actions">
                  <div className="selected-number">
                    <p
                      style={{
                        color: "#0C376A",
                        fontSize: "14px",
                        fontWeight: "bold",
                        marginBottom: 0,
                      }}
                    >
                      {selectedContinueItems.size === continueItems.length &&
                      continueItems.length !== 0
                        ? "All Selected"
                        : selectedContinueItems.size === 0
                        ? "None Selected"
                        : "Multiple Selected"}
                    </p>
                  </div>
                  <button
                    onClick={handleContinueSelectAll}
                    className="btn btn-primary"
                  >
                    {selectedContinueItems.size === continueItems.length &&
                    continueItems.length !== 0 ? (
                      <img
                        src={selectedCheck}
                        alt="All selected"
                        width={16}
                        height={16}
                      />
                    ) : (
                      <img
                        src={selectAllUnselected}
                        alt="Not selected"
                        width={16}
                        height={16}
                      />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      // Bulk move from continue to main data
                      const selectedItemsArray = continueItems.filter((item) =>
                        selectedContinueItems.has(item.id)
                      );
                      onUpdateData([...data, ...selectedItemsArray]);
                      setContinueItems(
                        continueItems.filter(
                          (item) => !selectedContinueItems.has(item.id)
                        )
                      );
                      setSelectedContinueItems(new Set());
                    }}
                    className="btn btn-success"
                    disabled={selectedContinueItems.size === 0}
                  >
                    <img
                      src={selectAllLeftArrow}
                      alt="Move back to main"
                      width={16}
                      height={16}
                    />
                  </button>
                </div>
              </div>
              <table className="data-table">
                <thead>
                  <tr style={{ height: "50px" }}></tr>
                </thead>
                <tbody>
                  {continueItems.map((item) => (
                    <tr key={item.id} className="table-row">
                      <td>{item.account_number}</td>
                      <td>{item.account_name}</td>
                      <td>
                        <div
                          onClick={() => {
                            const newSelectedContinueItems = new Set(
                              selectedContinueItems
                            );
                            if (newSelectedContinueItems.has(item.id)) {
                              newSelectedContinueItems.delete(item.id);
                            } else {
                              newSelectedContinueItems.add(item.id);
                            }
                            setSelectedContinueItems(newSelectedContinueItems);
                          }}
                          style={{ cursor: "pointer", textAlign: "center" }}
                        >
                          <img
                            src={
                              selectedContinueItems.has(item.id)
                                ? selectedCheck
                                : unselectedCheck
                            }
                            alt="Select"
                            style={{ width: "24px", height: "24px" }}
                          />
                        </div>
                      </td>
                      <div className="actions">
                        <button
                          className="btn-action"
                          onClick={() => handleContinueToMain(item)}
                          style={{
                            position: "absolute",
                            left: "-30px",
                            top: "25%",
                          }}
                        >
                          <img
                            src={leftBtn}
                            alt="Move to main"
                            width={20}
                            height={20}
                          />
                        </button>
                      </div>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DropZone>
        </div>
      </div>
    </DndProvider>
  );
};

export default DragDropTable;
